/** Response shapes of the admin_dash_* RPCs (supabase/migrations/20261030090000_admin_dashboard_rebuild.sql in the app repo). */
import type { Split } from './market';
import type { DayRow } from '../components/charts/DailySplit';

export interface Overview {
  generated_at: string;
  signups: { total: Split; today: Split; yesterday: Split; d7: Split; prev7: Split; d30: Split };
  active: { d1: Split; d7: Split; d30: Split };
  payers: {
    subscribers: Split; monthly: Split; season_pass: Split; cancelling: Split; past_due: Split;
    lapsed: number; comped: number; pack_holders: Split; packs_sold_30d: Split;
  };
  vyo: { today: Split; d7: Split; senders_7d: Split };
  sessions_7d: { uk: number; us: number; failed: number };
  psy_runs_7d: Split;
  offers: { total: number; d30: number };
  emails_today: { lifecycle: number; alerts: number; welcome: number };
  switches: { firm_alerts_live: boolean; lifecycle_emails_live: boolean; referral_rewards_live: boolean } | null;
  deletion_pending: number;
  dispatch_pending: number;
  dispatch_errors_30d: number;
  held_events: number;
  open_programmes: Split;
  latest_signups: { id: string; full_name: string | null; university: string | null; market: string; created_at: string; self_source: string; channel: string }[];
  latest_purchases: { user_id: string; kind: 'pack' | 'subscription'; plan: string | null; market: string; at: string; firm_name: string | null }[];
  latest_outcomes: { user_id: string; outcome: string; firm_name: string; career_stage: string | null; created_at: string }[];
}

export interface UserRow {
  id: string; email: string | null; full_name: string | null; university: string | null; market: string;
  created_at: string; last_sign_in_at: string | null; last_active: string | null; onboarded: boolean;
  class: string; plan: string | null; cancel_at_period_end: boolean; stripe_linked: boolean;
  paid_packs: number; comped_packs: number; payer: boolean; ever_paid: boolean;
  self_source: string; channel: string; deletion_pending: boolean;
}
export interface Paged<T> { page: number; per: number; total: number; rows: T[] }

export interface SessionRow {
  id: string; user_id: string; email: string | null; type: string; status: string; title: string | null;
  overall_score: number | null; duration_seconds: number | null; created_at: string; completed_at: string | null;
  failure_reason: string | null; shared: boolean; market: string;
}
export interface SessionTypeRow {
  type: string; d7_uk: number; d7_us: number; d30: number; d30_uk: number; d30_us: number;
  failed_30d: number; completed_30d: number; all_time: number;
}
export interface SessionsResp extends Paged<SessionRow> { by_type: SessionTypeRow[] }

export interface VyoResp {
  days: number;
  month_start: string;
  totals: {
    today: Split; d7: Split; window: Split; month: Split; all_time: Split;
    senders_7d: Split; senders_window: Split; pack_window: number; conversations_window: number;
  };
  daily: DayRow[];
  top_users: { user_id: string; email: string | null; market: string; n_window: number; n_pack: number; n_lifetime: number; paid_tier: boolean; ledger_used: number; ledger_limit: number; pack_vyo_used?: number }[];
  cap: { free_at_cap: number; free_near_cap: number; paid_over_150: number; paid_at_cap: number };
  conversations: { id: string; user_id: string; email: string | null; title: string | null; firm_id: string | null; firm_name: string | null; updated_at: string; market: string; user_messages: number }[];
}

export interface OutcomeSummary {
  offers: number; rejections: number; withdrew: number; offers_7d: number; offers_30d: number; offers_365d: number;
  users_with_offer: number; users_logging: number;
}
export interface OutcomesResp {
  summary: { uk: OutcomeSummary; us: OutcomeSummary };
  by_firm: { firm_name: string; offers: number; rejections: number; offers_uk: number; offers_us: number }[];
  by_university: { university: string; offers: number }[];
  by_programme: { career_stage: string; offers: number }[];
  rejection_stages: { stage: string; n: number }[];
  daily_offers_90d: DayRow[];
  compensation: { uk: { n: number; median: number | null }; us: { n: number; median: number | null } };
  recent: { id: string; user_id: string; outcome: string; firm_name: string; industry: string | null; career_stage: string | null; rejection_stage: string | null; compensation_gbp: number | null; shared_publicly: boolean; created_at: string; market: string }[];
}

export interface FunnelRow { signed_up: number; onboarded: number; reached_feature: number; saw_paywall: number; started_checkout: number; paid: number; signed_up_since_tracking: number }
export interface GrowthResp {
  days: number; from: string; tracking_from: string;
  totals: { all_time: Split; window: Split; d7: Split; prev7: Split };
  daily: DayRow[];
  self_reported: { source: string; signups: number; uk: number; us: number; onboarded: number; paid: number }[];
  attribution: { channel: string; signups: number; uk: number; us: number; paid: number }[];
  cross: { self_source: string; channel: string; n: number }[];
  other_answers: { answer: string; n: number }[];
  funnel: { uk: FunnelRow; us: FunnelRow };
  cohorts: { week: string; signed_up: number; uk: number; us: number; onboarded: number; reached_feature: number; started_checkout: number; paid: number }[];
  active: { d1: Split; d7: Split; d30: Split };
  universities: { label: string; n: number }[]; universities_answered: number;
  industries: { label: string; n: number }[]; industries_answered: number;
  demos: { run: number; claimed: number; by_feature: { feature: string; n: number }[] };
  recent: { id: string; created_at: string; market: string; self_source: string; channel: string; self_other: string | null; landing_page: string | null; referrer: string | null; campaign: string | null; paid: boolean }[];
}

export interface ComplianceResp {
  shares_total: number;
  shares: { id: string; user_id: string; email: string | null; type: string; title: string | null; shared_at: string | null }[];
  profiles: { total: number; phone_verified: number; phone_required: number; phone_override: number; tos_accepted: number };
  tos_versions: { version: string; n: number }[];
  deletions: { pending: number; completed: number; oldest_pending: string | null };
}

export interface RevenueResp {
  classes: { class: string; plan: string | null; market: string; stripe_linked: boolean; n: number }[];
  attention: { user_id: string; email: string | null; class: string; plan: string | null; market: string; current_period_end: string | null }[];
  subscriptions_started: { d30: Split };
  packs: { market: string; tier: string; paid: number; paid_30d: number; refunded: number; comped: number }[];
  latest_passes: { id: string; user_id: string; email: string | null; firm_name: string | null; tier: string; market: string; granted_at: string | null; expires_at: string | null; paid: boolean; refunded: boolean; has_session: boolean }[];
  paywall_triggers: { trigger: string; n: number }[];
  webhook_log: null | {
    last_24h: { event_type: string; status: string; n: number }[];
    last_received: string | null;
    errors: { event_id: string; event_type: string; error: string | null; http_status: number | null; attempts: number | null; last_received_at: string }[];
  };
}

export interface PsychometricsResp {
  days: number; all_time: number; d7: number;
  window: { runs: number; uk: number; us: number; users: number; completed: number; abandoned: number; avg_score: number | null };
  daily: DayRow[];
  by_provider: { label: string; n: number }[];
  by_test: { slug: string; name: string; n: number; abandoned: number }[];
  by_firm: { label: string; n: number }[];
  active_tests: number; calibrated_rows: number;
  recent: { id: string; user_id: string; firm_name: string | null; test_name: string | null; status: string; score: number | null; created_at: string; market: string }[];
}

export interface EmailVolumeResp {
  days: number;
  today: { lifecycle: number; alerts: number; welcome: number };
  daily: { day: string; lifecycle: number; alerts: number; welcome: number }[];
  by_label: { system: string; label: string; n: number }[];
  peak_day: { day: string; n: number } | null;
}

/** One London day x market from the live admin_daily_metrics (tracking work, 29-30 Oct). Money in minor units. */
export interface DailyMetric {
  day: string; market: 'uk' | 'us';
  visitors: number; signups: number; onboarded: number; first_feature: number;
  paywall_viewers: number; checkout_starters: number; stripe_sessions: number;
  purchases: number; first_purchases: number;
  new_revenue_gbp: number; new_revenue_usd: number; renewal_revenue_gbp: number; renewal_revenue_usd: number;
  refunds_gbp: number; refunds_usd: number;
  renewals: number; cancel_requests: number; subscriptions_ended: number; payment_failures: number; dunning_recovered: number; errors: number;
}
export interface AnalyticsAlert { code: string; severity: 'warn' | 'crit' | 'info'; message: string; detail: Record<string, unknown> | null }

/** Sum a numeric key of daily metrics, optionally for one market. */
export function sumMetric(rows: DailyMetric[] | undefined, key: keyof DailyMetric, market?: 'uk' | 'us'): number {
  return (rows ?? []).filter(r => !market || r.market === market).reduce((a, r) => a + (Number(r[key]) || 0), 0);
}
